import React from 'react';
import { Select } from 'evergreen-ui';
import { genericReactMemo } from 'helper/react';
import { CommonSelectProps } from './types';

type SelectObjectByIdProps<T> = {
  data: T[],
  isLoading: boolean
  optionLabel: (item: T) => string
} & CommonSelectProps

function SelectObjectByIdDefault<T extends { id?:number }>({
  selected,
  onChangeValue,
  firstOptionLabel,
  optionLabel,
  isLoading,
  data = [],
  ...selectProps
}: SelectObjectByIdProps<T>) {
  const thisFirstOptionLabel = React.useMemo(() => {
    if (isLoading) return 'Загрузка..';
    if (data.length === 0) return 'Нет данных';
    return firstOptionLabel || 'Выберите..';
  }, [data.length, firstOptionLabel, isLoading]);

  const thisOnSelect = React.useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;
    if (onChangeValue) onChangeValue(parseInt(value, 10));
  }, [onChangeValue]);

  return (
    <Select
      {...selectProps}
      disabled={selectProps.disabled || isLoading}
      value={selected}
      onChange={thisOnSelect}
    >
      <option value="">{thisFirstOptionLabel}</option>
      {
        React.Children.toArray(data.map((item) => (
          <option value={item.id}>
            {optionLabel(item)}
          </option>
        )))
      }
    </Select>
  );
}

/**
 * Компонент выбора объекта из массива данных по айди, где значение option является object.id,
 * т.к. id обязательно
 * @param selected айди объкта
 * @param onSelect (itemId: number, item: object) => void
 * @param {string} firstOptionLabel значение для первой опции выбора
 * @param {(item) => string} optionLabel (item) => string
 * @param {boolean} isLoading
 * @param {object[]} data массив объектов, где id обязательное свой-во
 */
export const SelectObjectById = genericReactMemo(SelectObjectByIdDefault);
(SelectObjectById as React.FC).displayName = 'SelectedObjectId';
